import React from 'react';
import { cn } from '@/lib/utils';

interface CircularProgressProps {
  value: number; // 0-100
  size?: number; // diameter in pixels
  strokeWidth?: number;
  className?: string;
  children?: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  showValue?: boolean;
  label?: string;
}

export function CircularProgress({
  value,
  size = 120,
  strokeWidth = 8,
  className,
  children,
  color = 'primary',
  showValue = true,
  label,
}: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;

  const getStrokeColor = () => {
    switch (color) {
      case 'success':
        return value >= 90
          ? 'stroke-green-500'
          : value >= 70
            ? 'stroke-blue-500'
            : 'stroke-orange-500';
      case 'warning':
        return 'stroke-orange-500';
      case 'danger':
        return 'stroke-red-500';
      case 'info':
        return 'stroke-blue-500';
      default:
        // Auto color based on value
        if (value >= 90) return 'stroke-green-500';
        if (value >= 70) return 'stroke-blue-500';
        if (value >= 50) return 'stroke-orange-500';
        return 'stroke-red-500';
    }
  };

  const getTextColor = () => {
    switch (color) {
      case 'success':
        return value >= 90
          ? 'text-green-600'
          : value >= 70
            ? 'text-blue-600'
            : 'text-orange-600';
      case 'warning':
        return 'text-orange-600';
      case 'danger':
        return 'text-red-600';
      case 'info':
        return 'text-blue-600';
      default:
        // Auto color based on value
        if (value >= 90) return 'text-green-600';
        if (value >= 70) return 'text-blue-600';
        if (value >= 50) return 'text-orange-600';
        return 'text-red-600';
    }
  };

  return (
    <div
      className={cn('relative', className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-muted/20"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={cn(
            getStrokeColor(),
            'transition-all duration-500 ease-in-out'
          )}
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children || (
          <>
            {showValue && (
              <span className={cn('text-2xl font-bold', getTextColor())}>
                {value}%
              </span>
            )}
            {label && (
              <span className="text-xs text-muted-foreground text-center mt-1">
                {label}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
